"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wp_query_params = {
  per_page: 3,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function FeaturedPropertiesConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="146952" className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#233554]">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="p-8 h-[400px] animate-pulse bg-[#112240]/20" />)}
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="p-8 text-center text-slate-400 text-sm border-t border-[#233554]">
        No upcoming certification cohorts available at the moment.
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#233554]">
      {products.slice(0, 3).map((product, i) => <article key={product.id} data-index={i} className="group flex flex-col bg-[#0a192f] hover:bg-[#112240] transition-colors duration-500">
          <Link to={product.link} className="block relative aspect-[4/3] overflow-hidden bg-[#112240] border-b border-[#233554]">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0 opacity-80 group-hover:opacity-100" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-slate-500">
                Course Visual
              </div>}
            <div className="absolute top-4 left-4">
              <Badge className="bg-[#64ffda] text-[#0a192f] rounded-none uppercase tracking-widest text-[10px] border-none px-3 py-1 font-bold">
                Professional Certification
              </Badge>
            </div>
          </Link>
          <div className="p-6 lg:p-8 flex flex-col flex-grow">
            <h4 className="text-lg font-semibold tracking-tight mb-3 text-white">
              {product.title}
            </h4>
            {product.short_description && <div className="text-sm text-slate-400 line-clamp-2 mb-8 font-sans leading-relaxed" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}
            <div className="mt-auto pt-6 border-t border-[#233554]">
              <AddToCartButton productId={product.id} variant="outline" className="w-full" buttonClassName="bg-[#64ffda] text-[#0a192f] rounded-none w-full uppercase tracking-widest text-xs font-bold h-12 border-none hover:bg-[#4de8c4] transition-colors" text="Enquire Now" showQuantity={false} />
            </div>
          </div>
        </article>)}
    </div>;
}
export default function FirmOverview() {
  const expertiseAreas = ["PMP Certification Preparation", "PRINCE2 Foundation & Practitioner", "PMI-ACP Agile Certification", "Outcome-Based Learning Models", "Senior Consultant Led Sessions"];
  return <section id="professionaltraining" className="bg-[#0a192f] text-white flex flex-col border-y border-[#233554]">
      {}
      <div className="grid grid-cols-1 lg:grid-cols-12 border-b border-[#233554]">
        <div className="p-8 border-b flex flex-col justify-center !border-transparent border-solid border-[#233554] lg:col-span-5 md:p-12 lg:p-16 xl:p-24 lg:border-b-0 lg:border-r">
          <Badge variant="outline" className="mb-8 self-start rounded-none border-[#64ffda]/30 tracking-widest uppercase text-[10px] text-[#64ffda] bg-[#64ffda]/5 px-4 py-1.5">
            Advance Your Career
          </Badge>
          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tighter mb-8 leading-[1.1] text-white">
            Master Your Project Leadership
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed mb-10 font-sans opacity-90">
            Projectora provides world-class training in PMP, PRINCE2, and PMI-ACP. Led by senior consultants, our courses are designed for Auckland's ambitious project professionals seeking globally recognized credentials and outcome-focused skills.
          </p>

          <div className="space-y-4 mb-12">
            {expertiseAreas.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-4 text-sm tracking-wide text-slate-200">
                <div className="w-1.5 h-1.5 bg-[#64ffda] rounded-none shadow-[0_0_8px_rgba(100,255,218,0.5)]" />
                <span>{item}</span>
              </div>)}
          </div>

          <Button asChild variant="outline" size="lg" className="self-start rounded-none border-[#64ffda] text-[#64ffda] hover:bg-[#64ffda] hover:text-[#0a192f] transition-all duration-300 uppercase tracking-widest text-xs font-bold h-12 px-8">
            <Link to="/AdvanceYourCareerPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive border shadow-xs has-[>svg]:px-4 self-start rounded-none border-[#64ffda] text-[#64ffda] transition-all duration-300 uppercase tracking-widest text-xs font-bold h-12 px-8 !bg-[hsl(194,70%,27%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 dark:bg-input/30 dark:border-input dark:hover:bg-input/50 hover:bg-[#64ffda] hover:text-[#0a192f]">
              Explore Our Programs <ArrowRight className="ml-3 size-4" />
            </Link>
          </Button>
        </div>

        <div className="lg:col-span-7 relative min-h-[500px] lg:min-h-full bg-[#112240]">
          <img src="https://climbing-leech-1.10web.cloud/wp-content/uploads/2026/03/Gemini_Generated_Image_uvrkcfuvrkcfuvrk.png" alt="Projectora Training Center Auckland" className="absolute inset-0 w-full h-full object-cover grayscale transition-all duration-1000 !opacity-[1] !border-transparent !text-transparent !border-none border-transparent hover:grayscale-0 hover:opacity-100" />
          <div className="absolute inset-0 pointer-events-none border-[20px] border-[#0a192f]/20 mix-blend-overlay" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a192f] via-transparent to-transparent hidden !border-none border-[rgb(255,255,255)] !opacity-[1] !text-transparent lg:block" src="https://climbing-leech-1.10web.cloud/wp-content/uploads/2026/03/Gemini_Generated_Image_uvrkcfuvrkcfuvrk.png" />
        </div>
      </div>

      {}
      <div className="flex flex-col">
        

        <EcommerceProductsProvider className="products-grid-wrapper" wp_query={{
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <FeaturedPropertiesConsumer />
        </EcommerceProductsProvider>
      </div>
      
    </section>;
}